import {
  borderWidth,
  color,
  fontFamily,
  radius,
  size,
  spacing,
  StyleVars,
  TRANSPARENT,
  TypographyName,
} from "./types";

/* -------------------------------------------------------------------- Badge */

export type BadgeSize = "small" | "medium";
export type BadgeTone = "default" | "grey" | "accent" | "new" | "outline" | "positive" | "warning" | "error";

export const badgeSpec = {
  defaults: { size: "medium" as BadgeSize, tone: "default" as BadgeTone },
  // Дизайн-ревью: надписи были прижаты к краям. Высота теперь считается от
  // паддингов и кегля, а не задаётся фиксированно.
  sizes: {
    small: {
      vars: {
        "--badge-padding-x": spacing("2"),
        "--badge-padding-y": spacing("0-5"),
      } satisfies StyleVars,
      typography: "caption-11-medium" as TypographyName,
    },
    medium: {
      vars: {
        "--badge-padding-x": spacing("2"),
        "--badge-padding-y": spacing("1"),
      } satisfies StyleVars,
      typography: "caption-12-medium" as TypographyName,
    },
  },
  tones: {
    default: { "--badge-bg": color("background-violet-10"), "--badge-fg": color("interaction-violet"), "--badge-border": TRANSPARENT },
    grey: { "--badge-bg": color("interaction-grey-light"), "--badge-fg": color("text-secondary-v2"), "--badge-border": TRANSPARENT },
    accent: { "--badge-bg": color("interaction-violet"), "--badge-fg": color("text-on-accent"), "--badge-border": TRANSPARENT },
    new: { "--badge-bg": color("purple-300"), "--badge-fg": color("text-on-accent"), "--badge-border": TRANSPARENT },
    outline: { "--badge-bg": TRANSPARENT, "--badge-fg": color("text-secondary-v2"), "--badge-border": color("border-tertiary") },
    positive: { "--badge-bg": color("status-positive"), "--badge-fg": color("status-accent-positive"), "--badge-border": TRANSPARENT },
    warning: { "--badge-bg": color("status-warning"), "--badge-fg": color("status-accent-warning"), "--badge-border": TRANSPARENT },
    error: { "--badge-bg": color("status-error"), "--badge-fg": color("status-accent-error"), "--badge-border": TRANSPARENT },
  } satisfies Record<BadgeTone, StyleVars>,
  base: {
    "--badge-radius": radius("full"),
    "--badge-border-width": borderWidth("hairline"),
    "--badge-gap": spacing("1"),
  } satisfies StyleVars,
};

/* --------------------------------------------------------------- HTTP badge */

export type HttpMethod = "GET" | "POST" | "DELETE";

export const methodBadgeSpec = {
  methods: {
    GET: { "--method-bg": color("method-get-bg"), "--method-fg": color("method-get-fg") },
    POST: { "--method-bg": color("method-post-bg"), "--method-fg": color("method-post-fg") },
    DELETE: { "--method-bg": color("method-delete-bg"), "--method-fg": color("method-delete-fg") },
  } satisfies Record<HttpMethod, StyleVars>,
  base: {
    "--method-radius": radius("md"),
    "--method-padding-x": spacing("1-5"),
    "--method-min-width": "48px",
    "--method-height": size("icon-lg"),
    "--method-font-family": fontFamily("mono"),
  } satisfies StyleVars,
  typography: "mono-11" as TypographyName,
};

/* --------------------------------------------------------------------- Chip */

export const chipSpec = {
  base: {
    "--chip-height": size("control-sm"),
    "--chip-padding-x": spacing("3"),
    "--chip-radius": radius("full"),
    "--chip-gap": spacing("1-5"),
    "--chip-bg": color("background-white"),
    "--chip-bg-hover": color("interaction-grey-light"),
    "--chip-fg": color("text-secondary-v2"),
    "--chip-border": color("border-secondary"),
    "--chip-border-width": borderWidth("hairline"),
    "--chip-bg-selected": color("background-violet-10"),
    "--chip-fg-selected": color("interaction-violet"),
    "--chip-border-selected": color("interaction-violet-light"),
  } satisfies StyleVars,
  typography: "caption-12-medium" as TypographyName,
};

/* ------------------------------------------------------------------ Divider */

export const dividerSpec = {
  base: {
    "--divider-color": color("text-tertiary"),
    "--divider-size": borderWidth("sm"),
    "--divider-line": color("border-secondary"),
  } satisfies StyleVars,
};

/* ------------------------------------------------------------------ Counter */

export const counterSpec = {
  base: {
    "--counter-height": size("control-sm"),
    "--counter-min-width": size("control-md"),
    "--counter-padding-x": spacing("2-5"),
    "--counter-radius": radius("md"),
    "--counter-bg": color("background-interaction-tertiary-hv"),
    "--counter-fg": color("text-secondary-v2"),
    "--counter-bg-limit": color("status-accent-warning"),
    "--counter-fg-limit": color("text-on-accent"),
  } satisfies StyleVars,
  typography: "caption-12-semibold" as TypographyName,
};

/* ------------------------------------------------------------------- Avatar */

export const avatarSpec = {
  base: {
    "--avatar-size": size("control-sm"),
    "--avatar-radius": radius("lg"),
    "--avatar-bg": color("background-violet-10"),
    "--avatar-fg": color("interaction-violet"),
  } satisfies StyleVars,
  typography: "caption-12-semibold" as TypographyName,
};

/* ------------------------------------------------------------------- Loader */

export type LoaderSize = "small" | "medium" | "large";

export const loaderSpec = {
  defaults: { size: "medium" as LoaderSize },
  sizes: {
    small: { "--loader-size": size("icon-sm") },
    medium: { "--loader-size": size("icon-lg") },
    large: { "--loader-size": size("control-md") },
  } satisfies Record<LoaderSize, StyleVars>,
  base: {
    "--loader-track": color("background-interaction-tertiary"),
    "--loader-indicator": color("interaction-violet"),
    "--loader-thickness": borderWidth("sm"),
    "--loader-duration": "900ms",
  } satisfies StyleVars,
};

/* --------------------------------------------------------------------- Link */

export const linkSpec = {
  base: {
    "--link-fg": color("interaction-violet"),
    "--link-fg-hover": color("interaction-violet-hv"),
    "--link-gap": spacing("1"),
  } satisfies StyleVars,
  typography: "text-14-medium" as TypographyName,
};

/* ------------------------------------------------------------------ Tooltip */

export const tooltipSpec = {
  base: {
    "--tooltip-bg": color("background-black"),
    "--tooltip-fg": color("text-on-black"),
    "--tooltip-radius": radius("lg"),
    "--tooltip-padding-x": spacing("2-5"),
    "--tooltip-padding-y": spacing("1-5"),
    "--tooltip-offset": spacing("2"),
    "--tooltip-max-width": "260px",
  } satisfies StyleVars,
  typography: "caption-11-medium" as TypographyName,
};

/* -------------------------------------------------------------------- Toast */

export type ToastTone = "default" | "success" | "warning" | "error";

export const toastSpec = {
  defaults: { tone: "default" as ToastTone },
  tones: {
    default: { "--toast-accent": color("text-on-black") },
    success: { "--toast-accent": color("status-accent-positive") },
    warning: { "--toast-accent": color("status-accent-warning") },
    error: { "--toast-accent": color("status-accent-error") },
  } satisfies Record<ToastTone, StyleVars>,
  base: {
    "--toast-bg": color("background-black"),
    "--toast-fg": color("text-on-black"),
    "--toast-subtitle": color("alpha-white-600"),
    "--toast-radius": radius("xl"),
    "--toast-padding-x": spacing("4"),
    "--toast-padding-y": spacing("3"),
    "--toast-gap": spacing("3"),
    "--toast-width": "288px",
    "--toast-offset": spacing("6"),
    "--toast-close-size": size("control-sm"),
    "--toast-close-bg-hover": color("alpha-white-50"),
  } satisfies StyleVars,
  titleTypography: "caption-12-semibold" as TypographyName,
  subtitleTypography: "caption-12-medium" as TypographyName,
  /** Тайминги из спецификации компонента в Figma. */
  timing: { short: 3000, long: 5000, titleLimit: 20, subtitleLimit: 40 },
};

/* ------------------------------------------------------------- Alert banner */

export type AlertTone = "info" | "success" | "warning" | "error";

export const alertBannerSpec = {
  defaults: { tone: "info" as AlertTone },
  tones: {
    info: { "--alert-bg": color("status-info"), "--alert-accent": color("status-accent-info") },
    success: { "--alert-bg": color("status-positive"), "--alert-accent": color("status-accent-positive") },
    warning: { "--alert-bg": color("status-warning"), "--alert-accent": color("status-accent-warning") },
    error: { "--alert-bg": color("status-error"), "--alert-accent": color("status-accent-error") },
  } satisfies Record<AlertTone, StyleVars>,
  base: {
    "--alert-radius": radius("2xl"),
    "--alert-padding": spacing("4"),
    "--alert-gap": spacing("3"),
    "--alert-fg": color("text-secondary-v2"),
  } satisfies StyleVars,
  titleTypography: "text-14-semibold" as TypographyName,
  textTypography: "caption-13-medium" as TypographyName,
};

/* --------------------------------------------------------------------- Card */

export const cardSpec = {
  base: {
    "--card-bg": color("background-white"),
    "--card-border": color("border-secondary"),
    "--card-border-width": borderWidth("hairline"),
    "--card-radius": radius("3xl"),
    "--card-padding": spacing("8"),
    "--card-shadow": "var(--shadow-soft)",
    "--card-gap": spacing("4"),
  } satisfies StyleVars,
};

/** Компактная карточка-плитка (каталог моделей, шаги quickstart). */
export const tileSpec = {
  base: {
    "--tile-bg": color("background-white"),
    "--tile-bg-hover": color("background-white"),
    "--tile-border": color("border-secondary"),
    "--tile-border-hover": color("interaction-violet-light"),
    "--tile-border-selected": color("interaction-violet"),
    "--tile-bg-selected": color("background-violet-10"),
    "--tile-border-width": borderWidth("hairline"),
    "--tile-radius": radius("2xl"),
    "--tile-padding": spacing("5"),
    "--tile-gap": spacing("2"),
    "--tile-shadow": "var(--shadow-soft)",
    "--tile-shadow-hover": "var(--shadow-float)",
  } satisfies StyleVars,
  titleTypography: "text-14-semibold" as TypographyName,
  textTypography: "caption-12-medium" as TypographyName,
};

/* -------------------------------------------------------------------- Table */

export const tableSpec = {
  base: {
    "--table-radius": radius("2xl"),
    "--table-border": color("border-secondary"),
    "--table-border-width": borderWidth("hairline"),
    "--table-head-bg": color("background-content-tertiary"),
    "--table-head-fg": color("text-secondary-v2"),
    "--table-cell-padding-x": spacing("4"),
    "--table-cell-padding-y": spacing("3"),
    "--table-row-bg-hover": color("background-content-tertiary"),
  } satisfies StyleVars,
  headTypography: "caption-12-medium" as TypographyName,
  cellTypography: "caption-13-medium" as TypographyName,
};

/* --------------------------------------------------------------- Code block */

export const codeBlockSpec = {
  base: {
    "--code-bg": color("background-black"),
    "--code-fg": color("code-fg"),
    "--code-fg-strong": color("text-on-black"),
    "--code-radius": radius("2xl"),
    "--code-padding": spacing("5"),
    "--code-action-offset": spacing("3"),
    "--code-inline-bg": color("background-gray-medium"),
    "--code-inline-fg": color("text-primary"),
    "--code-inline-radius": radius("md"),
    // шапка блока: название языка + кнопка копирования
    "--code-head-bg": color("alpha-white-50"),
    "--code-head-fg": color("alpha-white-600"),
    "--code-head-padding-x": spacing("4"),
    "--code-head-padding-y": spacing("3"),
    "--code-gutter-fg": color("alpha-white-400"),
    "--code-gutter-gap": spacing("4"),
  } satisfies StyleVars,
  typography: "mono-12" as TypographyName,
  headTypography: "caption-11-medium" as TypographyName,
};

/* ---------------------------------------------------------------- Accordion */

export const accordionSpec = {
  base: {
    "--accordion-border": color("border-secondary"),
    "--accordion-border-width": borderWidth("hairline"),
    "--accordion-radius": radius("2xl"),
    "--accordion-padding": spacing("4"),
    "--accordion-gap": spacing("3"),
    "--accordion-bg": color("background-white"),
    "--accordion-bg-hover": color("background-content-tertiary"),
  } satisfies StyleVars,
  titleTypography: "text-14-semibold" as TypographyName,
};
